import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { formatCurrency, formatDate } from "utils/helper";
import PageWrapper from "components/PageWrapper";
import { getSalesOrderHistory } from "actions/ordersAction";
import Spinner from "components/Spinner";
import { ArrowLeft, Receipt, MapPin, Wallet, Truck, ExternalLink } from "lucide-react";

const SalesOrderHistory = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const saleId = state?.saleId;
  const distributor = state?.distributor;
  const [loader, setLoader] = useState(false);
  const [salesHistory, setSalesHistory] = useState(null);

  const onLoad = async () => {
    try {
      setLoader(true);
      const response = await getSalesOrderHistory({ saleId, distributor });
      console.log({ response });
      if (response.status) {
        const { data } = response?.data;
        setSalesHistory(data);
      } else {
        setSalesHistory(null);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (saleId) onLoad();
  }, [saleId]);

  const ship = salesHistory?.shipping_address;
  const bill = salesHistory?.billing_address;
  const InfoBlock = ({ Icon, title, accent, children }) => (
    <div className={`flex flex-col gap-2.5 p-3.5 bg-s360-bg2 rounded-md border-t-2 ${accent}`}>
      <h4 className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3 m-0">
        <Icon className="w-3 h-3" /> {title}
      </h4>
      {children}
    </div>
  );
  const KV = ({ k, v, mono }) => (
    <div className="flex flex-col">
      <span className="text-[11px] text-s360-fg3">{k}</span>
      <span className={`text-[13px] font-semibold text-s360-fg1 ${mono ? "font-mono" : ""}`}>{v}</span>
    </div>
  );

  return (
    <PageWrapper>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-[28px] leading-[34px] font-bold tracking-[-0.015em] text-s360-ink">
            Sales order history
          </h1>
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-white border border-s360-stroke1 text-[13px] font-semibold text-s360-fg1 hover:bg-s360-bg2"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
        </div>

        {loader ? (
          <div className="flex justify-center items-center py-12">
            <Spinner />
          </div>
        ) : (
          <>
            {/* Line items */}
            <div className="bg-white rounded-md shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr>
                      {["Part number", "Description", "Manufacturer", "Qty", "Unit price", "Extended price"].map(
                        (h, i) => (
                          <th
                            key={h}
                            className={`py-2.5 px-3.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3 bg-s360-bg2 border-b border-s360-stroke1 whitespace-nowrap ${
                              i >= 3 ? "text-right" : "text-left"
                            }`}
                          >
                            {h}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {salesHistory?.order_details?.length > 0 &&
                      salesHistory?.order_details?.map((detail, index) => (
                        <tr key={index} className="border-b border-s360-bg2 last:border-0 hover:bg-s360-stripe">
                          <td className="py-3.5 px-3.5">
                            <div className="flex flex-col gap-0.5">
                              <span className="font-mono text-[13px] font-semibold text-s360-violet">
                                {detail.part_number}
                              </span>
                              <span className="font-mono text-[11px] text-s360-fg3">
                                {detail.mfr_part_number}
                              </span>
                            </div>
                          </td>
                          <td className="py-3.5 px-3.5">
                            <div className="max-w-[300px] truncate text-xs text-s360-fg2" title={detail.description}>
                              {detail.description}
                            </div>
                          </td>
                          <td className="py-3.5 px-3.5 text-[13px] text-s360-fg2">{detail.manufacturer}</td>
                          <td className="py-3.5 px-3.5 text-right font-mono text-[13px] text-s360-fg1 tabular-nums">
                            {detail.qty}
                          </td>
                          <td className="py-3.5 px-3.5 text-right font-mono text-[13px] text-s360-fg1 tabular-nums">
                            {formatCurrency(detail.unit_price)}
                          </td>
                          <td className="py-3.5 px-3.5 text-right font-mono text-[13px] font-bold text-s360-ink tabular-nums">
                            {detail.extended_price}
                          </td>
                        </tr>
                      ))}
                    {(!salesHistory?.order_details || salesHistory?.order_details?.length === 0) && (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-s360-fg3">
                          <div className="flex flex-col items-center gap-2">
                            <div className="text-lg font-medium text-s360-fg1">No data found</div>
                            <div className="text-sm">No order details available</div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {salesHistory && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3.5 px-[18px] py-4 border-t border-s360-stroke1 bg-gradient-to-b from-[#FBFAFF] to-s360-violet-50">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-s360-fg3">
                      Merchandise
                    </span>
                    <span className="font-mono font-bold text-lg text-s360-ink tabular-nums">
                      {salesHistory?.summary_detail?.MerchandiseTotal || 0}
                    </span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-s360-fg3">
                      Total
                    </span>
                    <span className="font-mono font-bold text-[22px] leading-[26px] text-s360-success tabular-nums">
                      {salesHistory?.summary_detail?.OrderTotal || 0}
                    </span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-s360-fg3">
                      Additional fees
                    </span>
                    <span className="font-mono font-bold text-lg text-s360-violet tabular-nums">
                      {salesHistory?.summary_detail?.AdditionalFeesTotal || 0}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Info grid */}
            {salesHistory && (
              <div className="bg-white rounded-md shadow-sm flex flex-col overflow-hidden">
                <div className="px-[18px] py-3.5 border-b border-s360-stroke1">
                  <h3 className="text-base font-semibold text-s360-fg1 m-0">Order information</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 p-4">
                  <InfoBlock Icon={Receipt} title="Order details" accent="border-t-s360-violet">
                    <div className="flex flex-col gap-1.5">
                      <KV k="Buyer" v={salesHistory?.buyer_name} />
                      <KV k="Sales order" v={salesHistory?.sales_order_id} mono />
                      <KV k="Web order" v={salesHistory?.web_order_id} mono />
                      <KV k="Status" v={salesHistory?.order_status_name} />
                      <KV k="Order date" v={formatDate(salesHistory?.order_date)} />
                      <KV k="Pending" v={salesHistory?.is_pending_order ? "Yes" : "No"} />
                      <KV k="Scheduled" v={salesHistory?.is_scheduled ? "Yes" : "No"} />
                    </div>
                  </InfoBlock>

                  <InfoBlock Icon={MapPin} title="Shipping address" accent="border-t-s360-teal">
                    {ship && (
                      <div className="text-xs leading-[17px] text-s360-fg2">
                        <span className="font-semibold text-s360-fg1 block mb-0.5">
                          {ship.first_name} {ship.last_name}
                        </span>
                        <div>{ship.company}</div>
                        <div>{ship.address_line}</div>
                        <div>{ship.address_line_one}</div>
                        {ship.address_line_two && <div>{ship.address_line_two}</div>}
                        <div>
                          {ship.city}, {ship.state_or_province} {ship.postal_code}
                        </div>
                        {ship.phone && (
                          <div className="mt-1">
                            <span className="font-semibold text-s360-fg1">Phone: </span>
                            {ship.phone}
                          </div>
                        )}
                      </div>
                    )}
                  </InfoBlock>

                  <InfoBlock Icon={MapPin} title="Billing address" accent="border-t-s360-info">
                    {bill && (
                      <div className="text-xs leading-[17px] text-s360-fg2">
                        <span className="font-semibold text-s360-fg1 block mb-0.5">
                          {bill.first_name} {bill.last_name}
                        </span>
                        <div>{bill.company}</div>
                        <div>{bill.address_line}</div>
                        <div>{bill.address_line_one}</div>
                        {bill.address_line_two && <div>{bill.address_line_two}</div>}
                        <div>
                          {bill.city}, {bill.state_or_province} {bill.postal_code}
                        </div>
                        {bill.phone && (
                          <div className="mt-1">
                            <span className="font-semibold text-s360-fg1">Phone: </span>
                            {bill.phone}
                          </div>
                        )}
                      </div>
                    )}
                  </InfoBlock>

                  <InfoBlock Icon={Wallet} title="Payment" accent="border-t-s360-warning">
                    <div className="flex flex-col gap-1.5">
                      <KV k="PO number" v={salesHistory?.payment_detail?.PoNumber} mono />
                      <KV k="Method" v={salesHistory?.payment_detail?.PaymentMethodName} />
                    </div>
                  </InfoBlock>

                  <InfoBlock Icon={Truck} title="Delivery & tracking" accent="border-t-s360-success">
                    <div className="flex flex-col gap-1.5">
                      <KV k="Shipping method" v={salesHistory?.delivery_detail?.ShippingMethodName} />
                      {salesHistory?.delivery_detail?.TrackingDetails?.[0] && (
                        <>
                          <KV
                            k="Tracking #"
                            v={salesHistory.delivery_detail.TrackingDetails[0].Number}
                            mono
                          />
                          <div className="flex flex-col">
                            <span className="text-[11px] text-s360-fg3">Carrier link</span>
                            <a
                              href={salesHistory.delivery_detail.TrackingDetails[0].Link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-s360-violet text-xs font-semibold hover:underline"
                            >
                              Track shipment <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        </>
                      )}
                    </div>
                  </InfoBlock>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </PageWrapper>
  );
};

export default SalesOrderHistory;
